import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardShell from "@/components/DashboardShell";
import ProjectCard from "@/components/ProjectCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function MasterProjects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [locality, setLocality] = useState("all");
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [claimedIds, setClaimedIds] = useState(new Set());
  const [selected, setSelected] = useState(new Set()); // project_ids
  const [claiming, setClaiming] = useState(false);

  const isAgent = user?.role === "agent";

  const refreshClaimed = async () => {
    if (!isAgent) return;
    try {
      const { data } = await api.get("/listings/claimed-project-ids");
      setClaimedIds(new Set(data.project_ids || []));
    } catch (_e) {
      /* silent — UI will just skip the claimed badge */
    }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/localities").then(({ data }) => setLocalities(data));
    api.get(`/master-projects`, { params: { zone: user.micro_market } })
      .then(({ data }) => setProjects(data));
    refreshClaimed();
  }, [user]);

  const filtered = useMemo(() => projects.filter((p) => {
    const m1 = locality === "all" || p.locality_tag === locality;
    const m2 = !search
      || p.project_name.toLowerCase().includes(search.toLowerCase())
      || p.developer.toLowerCase().includes(search.toLowerCase());
    return m1 && m2;
  }), [projects, locality, search]);

  const claimableFiltered = useMemo(
    () => filtered.filter((p) => !claimedIds.has(p.project_id)),
    [filtered, claimedIds],
  );

  const toggleOne = (pid) => {
    if (claimedIds.has(pid)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(new Set(claimableFiltered.map((p) => p.project_id)));
  };
  const clearSelection = () => setSelected(new Set());

  const handleBulkClaim = async () => {
    if (selected.size === 0) return;
    setClaiming(true);
    try {
      const { data } = await api.post("/listings/bulk-claim-master", {
        project_ids: Array.from(selected),
      });
      const c = data?.counts || {};
      const bits = [];
      if (c.claimed) bits.push(`${c.claimed} added`);
      if (c.already_claimed) bits.push(`${c.already_claimed} already in portal`);
      if (c.not_found) bits.push(`${c.not_found} not found`);
      toast.success(bits.length ? bits.join(" · ") : "Bulk claim complete.");
      await refreshClaimed();
      setSelected(new Set());
      if (c.claimed > 0) navigate("/dashboard/listings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk claim failed. Please try again.");
    } finally {
      setClaiming(false);
    }
  };

  const allVisibleSelected = claimableFiltered.length > 0
    && claimableFiltered.every((p) => selected.has(p.project_id));

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">MASTER · {user?.micro_market?.toUpperCase()}</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Top new launches in your <span className="font-serif-italic text-sg-gold-deep">corridor.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        These are the projects you can directly market on your branded portal. Tick multiple and add them to your portal in one click.
      </p>

      <div className="flex flex-col md:flex-row gap-3 mt-8 items-stretch md:items-center">
        <Input
          placeholder="Search by project or developer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:max-w-xs rounded-full bg-white border-sg-line"
          data-testid="projects-search-input"
        />
        <Select value={locality} onValueChange={setLocality}>
          <SelectTrigger className="md:w-56 rounded-full bg-white border-sg-line" data-testid="projects-locality-filter">
            <SelectValue placeholder="All localities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All localities</SelectItem>
            {(localities[user?.micro_market] || []).map((l) => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-sm text-sg-mute flex items-center px-2 font-mono">
          {filtered.length} of {projects.length}
        </div>
        {isAgent && claimableFiltered.length > 0 && (
          <button
            type="button"
            onClick={allVisibleSelected ? clearSelection : selectAllVisible}
            className="ml-auto text-xs uppercase tracking-widest text-sg-gold-deep hover:text-sg-navy transition-colors font-mono"
            data-testid="projects-toggle-select-all"
          >
            {allVisibleSelected ? "Clear selection" : `Select all ${claimableFiltered.length} visible`}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 pb-32">
        {filtered.map((p) => {
          const alreadyClaimed = claimedIds.has(p.project_id);
          const isSelected = selected.has(p.project_id);
          return (
            <div
              key={p.project_id}
              className="relative"
              data-testid={`master-project-wrap-${p.project_id}`}
            >
              {isAgent && (
                alreadyClaimed ? (
                  <div
                    className="absolute -top-2 -right-2 z-30 inline-flex items-center gap-1.5 rounded-full bg-sg-navy text-sg-gold border border-sg-gold/60 px-3 py-1 text-[10px] uppercase tracking-widest font-mono shadow-lg"
                    data-testid={`master-project-claimed-badge-${p.project_id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> In your Portal
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); toggleOne(p.project_id); }}
                    className={`absolute -top-2 -right-2 z-30 flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all shadow-lg ${
                      isSelected
                        ? "bg-sg-gold border-sg-gold text-sg-navy scale-110"
                        : "bg-white border-sg-navy/70 text-transparent hover:border-sg-gold hover:text-sg-gold-deep hover:scale-105"
                    }`}
                    aria-label={isSelected ? "Deselect" : "Select"}
                    aria-pressed={isSelected}
                    data-testid={`master-project-select-${p.project_id}`}
                  >
                    <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                )
              )}
              <div
                className={`transition-all rounded-2xl ${
                  isSelected ? "ring-2 ring-sg-gold ring-offset-2 ring-offset-sg-cream" : ""
                }`}
              >
                <ProjectCard project={p} tag="NEW LAUNCH" testIdPrefix="master-project" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating bulk-claim action bar */}
      {isAgent && selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]"
          data-testid="bulk-claim-bar"
        >
          <div className="flex items-center gap-4 bg-sg-navy text-sg-cream rounded-full shadow-2xl border border-sg-gold/40 px-3 py-2 pl-6">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-sg-gold" />
              <span className="font-mono">
                <strong className="text-sg-gold">{selected.size}</strong> selected
              </span>
            </div>
            <button
              type="button"
              onClick={clearSelection}
              className="text-white/70 hover:text-white transition-colors text-xs uppercase tracking-widest font-mono"
              data-testid="bulk-claim-clear-btn"
            >
              <X className="w-4 h-4 inline" /> Clear
            </button>
            <button
              type="button"
              onClick={handleBulkClaim}
              disabled={claiming}
              className="pill-solid inline-flex items-center gap-2 bg-sg-gold text-sg-navy hover:brightness-95 disabled:opacity-70 px-5"
              data-testid="bulk-claim-submit-btn"
            >
              {claiming
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <CheckCircle2 className="w-4 h-4" />}
              {claiming
                ? "Adding to your Portal…"
                : `Add ${selected.size} to my Portal`}
            </button>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
